import React, { useReducer } from "react"
import PropTypes from "prop-types"
import { initialState, WrapperModalsReducer } from "./WrapperModalsReducer"
import WrapperModalsContext from "./WrapperModalsContext"
import {
  SET_BC3_LINK_STATE,
  SET_NEW_MODAL_PART_STATE,
  SET_PART_BROWSER_STATE,
} from "context/types"

const WrapperModalsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(WrapperModalsReducer, initialState)

  /**
   * Updates the newModalPartidas state with new values.
   * This function allows updating a single property or multiple properties of the state.
   *
   * @param {string|object} keyOrObject - If a string is provided, it represents the key of the state to update. If an object is provided, it represents multiple key-value pairs to update. Example object:
   * ```javascript
   * {
   *   open: false,
   *   from: "wrapper modals",
   *   chapterId: null,
   *   partida: null,
   *   bancoDePrecios: "",
   *   srcPage: "",
   * }
   * ```
   * @param {*} [value] - The value to set for the key specified in the first argument. This is ignored if the first argument is an object.
   *
   * @example
   * // Update a single property
   * setNewModalPartState("open", true);
   *
   * @example
   * // Update multiple properties
   * setNewModalPartState({
   *   open: false,
   *   from: "wrapper modals",
   *   chapterId: null,
   *   partida: null,
   *   bancoDePrecios: "",
   *   srcPage: "",
   * });
   *
   * @returns {void}
   */
  const setNewModalPartState = (keyOrObject, value) => {
    if (typeof keyOrObject === "object") {
      // Si el primer argumento es un objeto, actualizamos múltiples propiedades
      dispatch({
        type: SET_NEW_MODAL_PART_STATE,
        payload: keyOrObject,
      })
    } else {
      // Si es una clave individual, actualizamos una sola propiedad
      dispatch({
        type: SET_NEW_MODAL_PART_STATE,
        payload: { [keyOrObject]: value },
      })
    }
  }

  const setBc3LinkState = (keyOrObject, value) => {
    if (typeof keyOrObject === "object") {
      // Si el primer argumento es un objeto, actualizamos múltiples propiedades
      dispatch({
        type: SET_BC3_LINK_STATE,
        payload: keyOrObject,
      })
    } else {
      // Si es una clave individual, actualizamos una sola propiedad
      dispatch({
        type: SET_BC3_LINK_STATE,
        payload: { [keyOrObject]: value },
      })
    }
  }

  const setPartBrowserState = payload => {
    dispatch({
      type: SET_PART_BROWSER_STATE,
      payload,
    })
  }

  return (
    <WrapperModalsContext.Provider
      value={{
        ...state,
        newModalPart: {
          ...state.newModalPart,
          setProperty: setNewModalPartState,
        },
        bc3Link: {
          ...state.bc3Link,
          setProperty: setBc3LinkState,
        },
        partBrowser: {
          ...state.partBrowser,
          setProperty: setPartBrowserState,
        },
      }}
    >
      {children}
    </WrapperModalsContext.Provider>
  )
}

WrapperModalsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default WrapperModalsProvider
